import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { hasPermission } from '../lib/permissions';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { useSortableTable } from '../hooks/useSortableTable';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { DatePicker } from '../components/ui/date-picker';
import { AsyncCustomerPicker } from '../components/AsyncCustomerPicker';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Search, Layers, Building2, MapPin, Calendar, ChevronDown, Check,
  GripVertical, Trash2, Pencil, Archive, X, Clock, User, Wrench, FileText,
  ListChecks, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

// ---------- helpers ----------

const statusColors = {
  active: 'bg-green-500/15 text-green-600 border-green-500/30',
  completed: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  archived: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30',
};

function fmtDate(d) {
  if (!d) return '-';
  try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; }
}

// ---------- component ----------

export default function Projects() {
  const { api, user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const canWrite = hasPermission(user, 'projects.write');

  // Gate: redirect if flag off
  useEffect(() => {
    if (!isEnabled('projects')) navigate('/', { replace: true });
  }, [isEnabled, navigate]);

  // ---- list state ----
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const listParams = useMemo(() => {
    const p = {};
    if (statusFilter && statusFilter !== 'all') p.status = statusFilter;
    return p;
  }, [statusFilter]);

  const projectList = useInfiniteList({
    endpoint: '/projects',
    search,
    params: listParams,
    enabled: isEnabled('projects'),
  });

  const { sortedData, requestSort, getSortDirection } = useSortableTable(projectList.items, {
    name: (p) => (p.name || '').toLowerCase(),
    customer: (p) => (p.customer_name || '').toLowerCase(),
    site: (p) => (p.site_address || '').toLowerCase(),
    start: (p) => p.start_date || '',
    end: (p) => p.end_date || '',
    tasks: (p) => `${p.task_done || 0}/${p.task_total || 0}`,
  });

  // ---- drawer state ----
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('details');
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // ---- form state ----
  const emptyForm = { name: '', customer_id: '', customer_name: '', site_id: '', start_date: '', end_date: '' };
  const [form, setForm] = useState(emptyForm);
  const [tasks, setTasks] = useState([]);        // existing tasks (from GET detail)
  const [newTasks, setNewTasks] = useState([]);   // new tasks added during creation
  const [siteSearch, setSiteSearch] = useState('');
  const [sitePopoverOpen, setSitePopoverOpen] = useState(false);
  const [customerSites, setCustomerSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  // ---- open drawer ----
  const openDrawer = async (project = null) => {
    setDrawerTab('details');
    if (project) {
      // Load full detail
      try {
        const { data } = await api.get(`/projects/${project.id}`);
        setEditingProject(data);
        setForm({
          name: data.name || '',
          customer_id: data.customer_id || '',
          customer_name: data.customer_name || '',
          site_id: data.site_id || '',
          start_date: data.start_date || '',
          end_date: data.end_date || '',
        });
        setTasks(data.tasks || []);
        if (data.customer_id) fetchSites(data.customer_id);
      } catch {
        toast.error('Failed to load project');
        return;
      }
    } else {
      setEditingProject(null);
      setForm(emptyForm);
      setTasks([]);
      setNewTasks([]);
      setCustomerSites([]);
    }
    setDrawerOpen(true);
  };

  // ---- fetch customer sites ----
  const fetchSites = useCallback(async (customerId) => {
    if (!customerId) { setCustomerSites([]); return; }
    setLoadingSites(true);
    try {
      const { data } = await api.get('/sites', { params: { customer_id: customerId } });
      setCustomerSites(Array.isArray(data) ? data : data.items || []);
    } catch { setCustomerSites([]); }
    finally { setLoadingSites(false); }
  }, [api]);

  // ---- customer change ----
  const handleCustomerChange = (id, customer) => {
    const name = customer?.company_name || customer?.contact_name || (typeof customer === 'string' ? customer : '');
    setForm(prev => ({ ...prev, customer_id: id, customer_name: name, site_id: '' }));
    setCustomerSites([]);
    if (id) fetchSites(id);
  };

  // ---- site helpers ----
  const siteName = (s) => {
    const parts = [s.street_address, s.suburb, s.state, s.postcode].filter(Boolean);
    return parts.length ? parts.join(', ') : s.name || 'Unnamed site';
  };
  const selectedSiteLabel = useMemo(() => {
    if (!form.site_id) return '';
    const s = customerSites.find(s => s.id === form.site_id);
    return s ? siteName(s) : '';
  }, [form.site_id, customerSites]);

  // ---- submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.customer_id) { toast.error('Customer is required'); return; }
    if (!form.start_date) { toast.error('Start date is required'); return; }
    if (!form.end_date) { toast.error('End date is required'); return; }
    if (form.end_date < form.start_date) { toast.error('End date must be on or after start date'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        customer_id: form.customer_id,
        site_id: form.site_id || null,
        start_date: form.start_date,
        end_date: form.end_date,
      };
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success('Project updated');
      } else {
        // Include any new tasks for creation
        if (newTasks.length > 0) {
          payload.tasks = newTasks.filter(t => t.description.trim()).map(t => ({ description: t.description.trim() }));
        }
        await api.post('/projects', payload);
        toast.success('Project created');
      }
      setDrawerOpen(false);
      projectList.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    } finally { setSaving(false); }
  };

  // ---- archive ----
  const handleArchive = async () => {
    if (!editingProject) return;
    setArchiving(true);
    try {
      await api.delete(`/projects/${editingProject.id}`);
      toast.success('Project archived');
      setDrawerOpen(false);
      setArchiveDialogOpen(false);
      projectList.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally { setArchiving(false); }
  };

  // ---- task CRUD (editing mode) ----
  const addTask = async () => {
    if (!editingProject) {
      // In create mode, add to local list
      setNewTasks(prev => [...prev, { _key: Date.now(), description: '' }]);
      return;
    }
    try {
      const { data } = await api.post(`/projects/${editingProject.id}/tasks`, { description: 'New task' });
      setTasks(prev => [...prev, data]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add task'); }
  };

  const updateTask = async (taskId, field, value) => {
    try {
      const { data } = await api.put(`/projects/${editingProject.id}/tasks/${taskId}`, { [field]: value });
      setTasks(prev => prev.map(t => t.id === taskId ? data : t));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update task'); }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/projects/${editingProject.id}/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete task'); }
  };

  const moveTask = async (taskId, direction) => {
    const idx = tasks.findIndex(t => t.id === taskId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= tasks.length) return;
    const newOrder = [...tasks];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setTasks(newOrder);
    // Persist sort_order
    try {
      await Promise.all([
        api.put(`/projects/${editingProject.id}/tasks/${newOrder[idx].id}`, { sort_order: idx }),
        api.put(`/projects/${editingProject.id}/tasks/${newOrder[swapIdx].id}`, { sort_order: swapIdx }),
      ]);
    } catch (err) { toast.error('Failed to reorder'); }
  };

  // ---- render ----
  if (!isEnabled('projects')) return null;

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Group related jobs under a project with trackable tasks</p>
        </div>
        {canWrite && (
          <Button className="btn-volt" onClick={() => openDrawer()} data-testid="create-project-btn">
            <Plus className="h-4 w-4 mr-2" />New Project
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="projects-search"
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="projects-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {projectList.loading && projectList.items.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-12" data-testid="projects-empty">
              <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No projects found</p>
              {canWrite && (
                <Button className="mt-4 btn-volt" onClick={() => openDrawer()} data-testid="create-project-empty-btn">
                  Create Your First Project
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead sortKey="name" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Name</SortableTableHead>
                    <SortableTableHead sortKey="customer" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Customer</SortableTableHead>
                    <SortableTableHead sortKey="site" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Site</SortableTableHead>
                    <SortableTableHead sortKey="start" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Start</SortableTableHead>
                    <SortableTableHead sortKey="end" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">End</SortableTableHead>
                    <SortableTableHead sortKey="tasks" requestSort={requestSort} getSortDirection={getSortDirection} className="label-overline">Tasks</SortableTableHead>
                    <TableHead className="label-overline">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((p) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openDrawer(p)}
                      data-testid={`project-row-${p.id}`}
                    >
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          {p.customer_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        {p.site_address ? (
                          <span className="flex items-center gap-1.5 text-sm text-muted-foreground max-w-[200px] truncate">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />{p.site_address}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm">{fmtDate(p.start_date)}</TableCell>
                      <TableCell className="text-sm">{fmtDate(p.end_date)}</TableCell>
                      <TableCell>
                        <span className="text-sm font-mono">
                          {p.task_done}/{p.task_total}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColors[p.status] || ''}>
                          {p.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div ref={projectList.sentinelRef} />
          {sortedData.length > 0 && (
            <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground" data-testid="projects-pagination-footer">
              <span>Showing {projectList.items.length} of {projectList.total}</span>
              {projectList.hasMore && (
                <Button size="sm" variant="outline" onClick={projectList.loadMore} data-testid="projects-load-more">Load more</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Drawer ===== */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[700px] sm:max-w-[700px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#ffd108]" />
              {editingProject ? editingProject.name : 'New Project'}
            </SheetTitle>
          </SheetHeader>

          {editingProject ? (
            <Tabs defaultValue="details" value={drawerTab} onValueChange={setDrawerTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2" data-testid="project-drawer-tabs">
                <TabsTrigger value="details" className="flex items-center gap-2" data-testid="project-tab-details">
                  <Pencil className="h-4 w-4" />Details
                </TabsTrigger>
                <TabsTrigger value="tasks" className="flex items-center gap-2" data-testid="project-tab-tasks">
                  <ListChecks className="h-4 w-4" />Tasks ({tasks.length})
                </TabsTrigger>
              </TabsList>

              {/* ----- Details Tab ----- */}
              <TabsContent value="details" className="mt-4">
                <ProjectDetailsForm
                  form={form}
                  setForm={setForm}
                  customerSites={customerSites}
                  loadingSites={loadingSites}
                  sitePopoverOpen={sitePopoverOpen}
                  setSitePopoverOpen={setSitePopoverOpen}
                  siteSearch={siteSearch}
                  setSiteSearch={setSiteSearch}
                  selectedSiteLabel={selectedSiteLabel}
                  siteName={siteName}
                  handleCustomerChange={handleCustomerChange}
                  handleSubmit={handleSubmit}
                  saving={saving}
                  canWrite={canWrite}
                  isEditing={true}
                  onCancel={() => setDrawerOpen(false)}
                />
                {/* Archive */}
                {canWrite && editingProject.status !== 'archived' && (
                  <div className="mt-8 pt-4 border-t">
                    <Button
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setArchiveDialogOpen(true)}
                      data-testid="archive-project-btn"
                    >
                      <Archive className="h-4 w-4 mr-2" />Archive Project
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* ----- Tasks Tab ----- */}
              <TabsContent value="tasks" className="mt-4">
                <TasksTab
                  tasks={tasks}
                  canWrite={canWrite}
                  projectStatus={editingProject.status}
                  onAdd={addTask}
                  onUpdate={updateTask}
                  onDelete={deleteTask}
                  onMove={moveTask}
                />
              </TabsContent>
            </Tabs>
          ) : (
            /* Create mode: details form + inline new tasks */
            <div className="mt-4">
              <ProjectDetailsForm
                form={form}
                setForm={setForm}
                customerSites={customerSites}
                loadingSites={loadingSites}
                sitePopoverOpen={sitePopoverOpen}
                setSitePopoverOpen={setSitePopoverOpen}
                siteSearch={siteSearch}
                setSiteSearch={setSiteSearch}
                selectedSiteLabel={selectedSiteLabel}
                siteName={siteName}
                handleCustomerChange={handleCustomerChange}
                handleSubmit={handleSubmit}
                saving={saving}
                canWrite={canWrite}
                isEditing={false}
                onCancel={() => setDrawerOpen(false)}
                newTasks={newTasks}
                setNewTasks={setNewTasks}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Archive Confirmation */}
      <ConfirmationDialog
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
        title="Archive Project"
        description={`Archive "${editingProject?.name}"? The project will be hidden from active lists but all data is preserved.`}
        confirmText="Archive"
        variant="archive"
        onConfirm={handleArchive}
        isLoading={archiving}
        testId="archive-project-dialog"
      />
    </div>
  );
}


// =====================================================
// ProjectDetailsForm — shared between create & edit
// =====================================================

function ProjectDetailsForm({
  form, setForm, customerSites, loadingSites,
  sitePopoverOpen, setSitePopoverOpen, siteSearch, setSiteSearch,
  selectedSiteLabel, siteName, handleCustomerChange,
  handleSubmit, saving, canWrite, isEditing, onCancel,
  newTasks, setNewTasks,
}) {
  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return customerSites;
    const q = siteSearch.toLowerCase();
    return customerSites.filter(s => siteName(s).toLowerCase().includes(q));
  }, [customerSites, siteSearch, siteName]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4" data-testid="project-details-form">
      {/* Name */}
      <div className="space-y-2">
        <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
        <Input
          data-testid="project-name-input"
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Highway 42 Expansion"
          required
        />
      </div>

      {/* Customer */}
      <div className="space-y-2">
        <Label className="label-overline">Customer <span className="text-red-500">*</span></Label>
        <AsyncCustomerPicker
          value={form.customer_id}
          selectedName={form.customer_name}
          onChange={(id, customer) => handleCustomerChange(id, customer)}
          testId="project-customer-picker"
        />
      </div>

      {/* Site */}
      <div className="space-y-2">
        <Label className="label-overline">Site</Label>
        <Popover open={sitePopoverOpen} onOpenChange={setSitePopoverOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between font-normal"
              data-testid="project-site-select"
              disabled={!form.customer_id}
            >
              {selectedSiteLabel ? (
                <span className="flex items-center gap-2 truncate">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {selectedSiteLabel}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {form.customer_id ? (loadingSites ? 'Loading sites...' : 'Select site (optional)') : 'Select customer first'}
                </span>
              )}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput
                placeholder="Search sites..."
                value={siteSearch}
                onValueChange={setSiteSearch}
              />
              <CommandList className="max-h-[250px] overflow-y-auto">
                <CommandEmpty>No sites found for this customer.</CommandEmpty>
                {form.site_id && (
                  <CommandGroup>
                    <CommandItem
                      value="__clear__"
                      onSelect={() => { setForm(prev => ({ ...prev, site_id: '' })); setSitePopoverOpen(false); }}
                      className="text-muted-foreground text-xs"
                      data-testid="project-site-clear"
                    >
                      <X className="mr-1.5 h-3 w-3" /> Clear site
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandGroup heading="Sites">
                  {filteredSites.map((s) => (
                    <CommandItem
                      key={s.id}
                      value={siteName(s)}
                      onSelect={() => { setForm(prev => ({ ...prev, site_id: s.id })); setSitePopoverOpen(false); }}
                      data-testid={`project-site-option-${s.id}`}
                    >
                      <Check className={`mr-2 h-4 w-4 ${form.site_id === s.id ? 'opacity-100' : 'opacity-0'}`} />
                      <MapPin className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                      {siteName(s)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="label-overline">Start Date <span className="text-red-500">*</span></Label>
          <DatePicker
            value={form.start_date}
            onChange={(d) => setForm(prev => ({ ...prev, start_date: d }))}
            testId="project-start-date"
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <Label className="label-overline">End Date <span className="text-red-500">*</span></Label>
          <DatePicker
            value={form.end_date}
            onChange={(d) => setForm(prev => ({ ...prev, end_date: d }))}
            testId="project-end-date"
            className="w-full"
          />
        </div>
      </div>

      {/* Inline tasks for create mode */}
      {!isEditing && (
        <div className="space-y-2 pt-2">
          <Label className="label-overline flex items-center gap-1.5">
            <ListChecks className="h-3.5 w-3.5" /> Initial Tasks (optional)
          </Label>
          {newTasks && newTasks.map((t, idx) => (
            <div key={t._key} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-6 text-right">{idx + 1}.</span>
              <Input
                data-testid={`new-task-input-${idx}`}
                value={t.description}
                onChange={(e) => {
                  const updated = [...newTasks];
                  updated[idx] = { ...updated[idx], description: e.target.value };
                  setNewTasks(updated);
                }}
                placeholder="Task description"
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => setNewTasks(prev => prev.filter((_, i) => i !== idx))}
                data-testid={`remove-new-task-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNewTasks(prev => [...(prev || []), { _key: Date.now(), description: '' }])}
            data-testid="add-new-task-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Task
          </Button>
        </div>
      )}

      {/* Actions */}
      <SheetFooter className="pt-4 border-t mt-6">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="project-cancel-btn">Cancel</Button>
        {canWrite && (
          <Button type="submit" className="btn-volt" disabled={saving} data-testid="project-submit-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing ? 'Update Project' : 'Create Project'}
          </Button>
        )}
      </SheetFooter>
    </form>
  );
}


// =====================================================
// TasksTab
// =====================================================

function TasksTab({ tasks, canWrite, projectStatus, onAdd, onUpdate, onDelete, onMove }) {
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editDesc, setEditDesc] = useState('');

  return (
    <div className="space-y-4" data-testid="project-tasks-tab">
      {canWrite && projectStatus !== 'archived' && (
        <div className="flex justify-end">
          <Button size="sm" className="btn-volt" onClick={onAdd} data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Task
          </Button>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="tasks-empty">
          <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No tasks yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task, idx) => {
            const isCompleted = task.status === 'completed';
            const isEditing = editingTaskId === task.id;
            const c = task.completion;

            return (
              <div
                key={task.id}
                className={`p-3 rounded-lg border ${isCompleted ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/30 border-border'}`}
                data-testid={`task-row-${task.id}`}
              >
                <div className="flex items-start gap-2">
                  {/* Reorder grip */}
                  {canWrite && !isCompleted && projectStatus !== 'archived' && (
                    <div className="flex flex-col gap-0.5 mt-1">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        onClick={() => onMove(task.id, 'up')}
                        disabled={idx === 0}
                        data-testid={`task-move-up-${task.id}`}
                      >
                        <GripVertical className="h-4 w-4 rotate-180" />
                      </button>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                        onClick={() => onMove(task.id, 'down')}
                        disabled={idx === tasks.length - 1}
                        data-testid={`task-move-down-${task.id}`}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Task content */}
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              onUpdate(task.id, 'description', editDesc);
                              setEditingTaskId(null);
                            }
                            if (e.key === 'Escape') setEditingTaskId(null);
                          }}
                          autoFocus
                          className="flex-1"
                          data-testid={`task-edit-input-${task.id}`}
                        />
                        <Button
                          size="sm"
                          className="btn-volt"
                          onClick={() => { onUpdate(task.id, 'description', editDesc); setEditingTaskId(null); }}
                          data-testid={`task-save-btn-${task.id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingTaskId(null)}
                          data-testid={`task-cancel-edit-${task.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground">{idx + 1}.</span>
                          <span className={`font-medium ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                            {task.description}
                          </span>
                          {isCompleted && <Badge variant="outline" className="text-xs bg-green-500/15 text-green-600 border-green-500/30">Completed</Badge>}
                        </div>

                        {/* Completion details */}
                        {isCompleted && c && (
                          <div className="mt-2 pl-6 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {c.operator_name && (
                              <span className="flex items-center gap-1"><User className="h-3 w-3" />{c.operator_name}</span>
                            )}
                            {c.job_date && (
                              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(c.job_date)}</span>
                            )}
                            {(c.start_time || c.finish_time) && (
                              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.start_time || '?'} - {c.finish_time || '?'}</span>
                            )}
                            {(c.start_smu != null || c.finish_smu != null) && (
                              <span className="flex items-center gap-1">
                                <Wrench className="h-3 w-3" />
                                SMU: {c.start_smu ?? '-'} → {c.finish_smu ?? '-'} {c.smu_unit || ''}
                              </span>
                            )}
                            {(c.start_smu_2 != null || c.finish_smu_2 != null) && (
                              <span className="flex items-center gap-1">
                                <Wrench className="h-3 w-3" />
                                SMU2: {c.start_smu_2 ?? '-'} → {c.finish_smu_2 ?? '-'} {c.smu_unit_2 || ''}
                              </span>
                            )}
                            {c.work_description && (
                              <span className="col-span-2 flex items-center gap-1"><FileText className="h-3 w-3" />{c.work_description}</span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  {canWrite && !isCompleted && projectStatus !== 'archived' && !isEditing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => { setEditingTaskId(task.id); setEditDesc(task.description); }}
                        data-testid={`task-edit-btn-${task.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(task.id)}
                        data-testid={`task-delete-btn-${task.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
